/**
 * URL / query helpers for Manage Listings hide filters.
 */

export const RESULT_PAGE_SIZE = 42;

export const MANAGE_LISTINGS_FILTERS_PROFILE_KEY = 'manageListingsFilters';

export const isManageListingsFilterOn = value => value === true;

/**
 * Whether stored ownListings query `states` means closed listings are hidden.
 *
 * @param {string[]|undefined|null} states
 * @returns {boolean}
 */
export const isHideClosedActiveFromStates = states =>
  Array.isArray(states) && !states.includes('closed');

/**
 * After closing a listing while hide-closed is on, drop it from the current page
 * and decrement pagination totals (avoids a follow-up query that can fail/desync).
 *
 * @param {Object} params
 * @param {Array<{ uuid: string }>} params.currentPageResultIds
 * @param {Object|null|undefined} params.pagination
 * @param {{ uuid: string }} params.closedListingId
 * @returns {{ currentPageResultIds: Array<{ uuid: string }>, pagination: Object|null|undefined }}
 */
export const removeClosedListingFromManageListingsPage = ({
  currentPageResultIds,
  pagination,
  closedListingId,
}) => {
  const closedUuid = closedListingId?.uuid;
  const nextIds = (currentPageResultIds || []).filter(id => id.uuid !== closedUuid);
  if (!pagination || pagination.totalItems == null) {
    return { currentPageResultIds: nextIds, pagination };
  }
  const totalItems = Math.max(0, pagination.totalItems - 1);
  const perPage = pagination.perPage || RESULT_PAGE_SIZE;
  const totalPages = totalItems === 0 ? 1 : Math.max(1, Math.ceil(totalItems / perPage));
  return {
    currentPageResultIds: nextIds,
    pagination: {
      ...pagination,
      totalItems,
      totalPages,
    },
  };
};

/**
 * Map hide-filter flags to Sharetribe ownListings `states`, or null to omit.
 *
 * @param {Object} params
 * @param {boolean} [params.hideClosed]
 * @param {boolean} [params.hideDrafts]
 * @returns {string[]|null}
 */
/**
 * Whether a loaded own listing should stay visible for the current hide toggles.
 * Used to filter an in-memory page instead of refetching.
 *
 * @param {Object} listing
 * @param {{ hideClosed?: boolean, hideDrafts?: boolean }} filters
 * @returns {boolean}
 */
export const listingVisibleWithManageFilters = (listing, { hideClosed, hideDrafts } = {}) => {
  const state = listing?.attributes?.state;
  if (isManageListingsFilterOn(hideClosed) && state === 'closed') {
    return false;
  }
  if (isManageListingsFilterOn(hideDrafts) && state === 'draft') {
    return false;
  }
  return true;
};

export const statesForManageListingsFilters = ({ hideClosed, hideDrafts }) => {
  const hideClosedOn = isManageListingsFilterOn(hideClosed);
  const hideDraftsOn = isManageListingsFilterOn(hideDrafts);
  if (!hideClosedOn && !hideDraftsOn) {
    return null;
  }
  let states = ['draft', 'pendingApproval', 'published', 'closed'];
  if (hideClosedOn) {
    states = states.filter(s => s !== 'closed');
  }
  if (hideDraftsOn) {
    states = states.filter(s => s !== 'draft');
  }
  return states;
};

/**
 * Build Manage Listings URL search params (only active filter keys + page>1).
 *
 * @param {Object} params
 * @param {boolean} [params.hideClosed]
 * @param {boolean} [params.hideDrafts]
 * @param {number} [params.page]
 * @returns {Object}
 */
export const manageListingsFilterSearchParams = ({ hideClosed, hideDrafts, page }) => {
  const params = {};
  if (isManageListingsFilterOn(hideClosed)) {
    params.hideClosed = true;
  }
  if (isManageListingsFilterOn(hideDrafts)) {
    params.hideDrafts = true;
  }
  if (page && page > 1) {
    params.page = page;
  }
  return params;
};

/**
 * Read saved Manage Listings hide filters from currentUser.privateData.
 *
 * @param {Object|null|undefined} currentUser
 * @returns {{ hideClosed: boolean, hideDrafts: boolean }}
 */
export const getManageListingsFiltersFromUser = currentUser => {
  const saved =
    currentUser?.attributes?.profile?.privateData?.[MANAGE_LISTINGS_FILTERS_PROFILE_KEY];
  if (!saved || typeof saved !== 'object') {
    return { hideClosed: false, hideDrafts: false };
  }
  return {
    hideClosed: isManageListingsFilterOn(saved.hideClosed),
    hideDrafts: isManageListingsFilterOn(saved.hideDrafts),
  };
};

/**
 * Per-key merge: present URL keys override; missing keys fall back to profile (then false).
 *
 * @param {Object} params
 * @param {Object} [params.queryParams] parsed location search
 * @param {Object|null|undefined} [params.currentUser]
 * @returns {{ hideClosed: boolean, hideDrafts: boolean }}
 */
export const resolveManageListingsFilters = ({ queryParams = {}, currentUser } = {}) => {
  const fromUser = getManageListingsFiltersFromUser(currentUser);
  return {
    hideClosed:
      'hideClosed' in queryParams
        ? isManageListingsFilterOn(queryParams.hideClosed)
        : fromUser.hideClosed,
    hideDrafts:
      'hideDrafts' in queryParams
        ? isManageListingsFilterOn(queryParams.hideDrafts)
        : fromUser.hideDrafts,
  };
};

/**
 * Normalize ownListings `states` for equality checks (null/undefined/absent ≡ no filter).
 *
 * @param {string[]|null|undefined} states
 * @returns {string}
 */
export const manageListingsStatesKey = states => (Array.isArray(states) ? states.join(',') : '');

/**
 * Whether an in-memory ownListings query already matches the next page + states.
 * Used to skip a redundant refetch when URL sync only adds filter params.
 *
 * @param {Object|null|undefined} prevQueryParams stored ManageListingsPage.queryParams
 * @param {{ page?: number, states?: string[]|null }} next
 * @returns {boolean}
 */
export const ownListingsQueryMatches = (prevQueryParams, { page, states } = {}) => {
  if (!prevQueryParams) {
    return false;
  }
  const prevPage = prevQueryParams.page || 1;
  const nextPage = page || 1;
  if (prevPage !== nextPage) {
    return false;
  }
  return manageListingsStatesKey(prevQueryParams.states) === manageListingsStatesKey(states);
};
